import { gql, useMutation } from '@apollo/client'
import { ChipRemovable, Heading, Modal, ModalOnOpenChangeOptions, useToast } from '@farol-ds/react'
import { useMetrics } from '@jusbrasil-web/docview/core'

import { EditDocumentTopics_DocumentFragment } from './__generated__/edit-document-topics.graphql'
import {
  AddTopicToDocumentMutation,
  RemoveTopicToDocumentMutation,
} from './__generated__/mutations.graphql'
import styles from './edit-document-topics.module.scss'
import { AddTopicDocument, RemoveTopicDocument } from './mutations'
import { SearchField } from './search-field'
import { Topic } from './types'

export interface EditDocumentTopicsProps {
  isOpen: boolean
  document: EditDocumentTopics_DocumentFragment
  topics: Topic[]
  onAddTopic?: (topic: Topic) => void
  onRemoveTopic?: (topic: Topic) => void
  onOpenChange?: (open: boolean, options: ModalOnOpenChangeOptions) => void
}

export function EditDocumentTopics(props: EditDocumentTopicsProps) {
  const { isOpen, document, topics, onAddTopic, onRemoveTopic, onOpenChange } = props

  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const [addTopicDocument] = useMutation<AddTopicToDocumentMutation>(AddTopicDocument)
  const [removeTopicDocument] = useMutation<RemoveTopicToDocumentMutation>(RemoveTopicDocument)
  const toast = useToast()

  const addTopic = (topic: Topic) => {
    addTopicDocument({
      variables: {
        input: {
          tid: topic.tid,
          docId: document.docId,
          artifact: document.artifact,
        },
      },
    })
      .then(() => {
        sendMetrics('Topic.Added')
        onAddTopic?.(topic)
      })
      .catch((e) => {
        console.error('Error adding topic:', e)
        toast('Não foi possível adicionar o tópico', { type: 'negative' })
      })
  }

  const removeTopic = (topic: Topic) => {
    removeTopicDocument({
      variables: {
        input: {
          tid: topic.tid,
          docId: document.docId,
          artifact: document.artifact,
        },
      },
    })
      .then(() => {
        sendMetrics('Topic.Removed')
        onRemoveTopic?.(topic)
      })
      .catch((e) => {
        console.error('Error removing topic:', e)
        toast('Não foi possível remover o tópico', { type: 'negative' })
      })
  }

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange}>
      <Modal.Content containerClassName={styles.modalContainer}>
        <Modal.IconClose />
        <Modal.Header>
          <Modal.HeaderTitle>Editar tópicos</Modal.HeaderTitle>
          <Modal.HeaderDescription>
            Quais os principais assuntos desse documento?
          </Modal.HeaderDescription>
        </Modal.Header>

        <Modal.Body ref={dispatcherRef}>
          <SearchField addTopic={addTopic} />

          {topics.length > 0 && (
            <>
              <Heading size="sm" className={styles.heading}>
                Já adicionados:
              </Heading>
              <div className={styles.chipsList}>
                {topics.map((topic) => (
                  <ChipRemovable
                    id={String(topic.tid)}
                    key={topic.tid}
                    onRemove={() => removeTopic(topic)}
                    title={topic.title}
                    unstable_truncate
                    className={styles.chip}
                  >
                    {topic.title}
                  </ChipRemovable>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal>
  )
}

EditDocumentTopics.fragments = {
  document: gql`
    fragment EditDocumentTopics_document on Document {
      docId
      artifact
    }
  `,
}
